<template>
  <VDialog
    :open="isOpen"
    :headline="title"
    modal
    non-dismissible
    full-width-body
    @close="closeModal"
    style="--dialog-min-inline-size: 580px; --dialog-max-inline-size: 580px;"
  >
    <template #body>
      <div class="gridMargin">
        <div class="vmb-20">
          <p class="vtext-subtitle vpb-10">Export format</p>
          <VTextField :modelValue="EXPORT_FORMAT" readonly placeholder="CSV" aria-label="csv" appearance="ghost" class="vivid-input" />
        </div>
        <div class="vmb-20">
          <p class="vtext-subtitle vpb-10">Delimiter</p>
          <selectDropDown :options="delimiterOptions" :selectedValueProp="selectedDelimiter" placeholder="Select a delimiter" @onSelectOption="({ selectedValue }) => selectedDelimiter = selectedValue" />
        </div>
        <div class="vmb-20" v-if="type === 'customers'">
          <p class="vtext-subtitle vpb-10">Filter type</p>
          <selectDropDown :options="filterTypeOptions" :selectedValueProp="selectedFilterType" placeholder="Select Filter Type" @onSelectOption="({ selectedValue }) => { selectedFilterType = selectedValue; onFilterTypeChange(); }" />
        </div>
        <div class="vmb-20" v-if="selectedFilterType !== 'noFilter'">
          <div class="flex-start">
            <p class="vtext-subtitle vpb-10">Filter value</p>
            <HelpTooltip v-if="selectedFilterType === 'user_mobilenumber_wo_plus'" floating placement="right" :iconStyle="{ 'padding-bottom': '8px' }" > Enter the number in E.164 format: +[country code] [number] (e.g., +65 90909090) </HelpTooltip>
          </div>
          <VTextField
            v-if="selectedFilterType === 'user_mobilenumber_wo_plus'"
            ref="mobileInputRef"
            type="tel"
            aria-label="filter value"
            :maxlength="18"
            :placeholder="filterPlaceholder"
            v-model="filterValue"
            @input="onMobileInput"
            appearance="ghost"
            class="vivid-input"
          />
          <selectDropDown
            v-else-if="selectedFilterType === 'marketing_opt_in_status'"
            :options="marketingOptInOptions"
            :selectedValueProp="filterValue"
            placeholder="Select marketing opt-in status"
            @onSelectOption="({ selectedValue }) => filterValue = selectedValue"
          />
          <template v-else-if="selectedFilterType === 'CustomFields'">
            <CustomFieldInputKeyValue
              v-model="filterValue"
              placeholder="Enter value"
              :isDisabled="false"
              :maxLength="240"
              class="input-wrapper"
            />
          </template>
          <template v-else-if="selectedFilterType === 'CustomTags'">
            <selectDropDown
              :options="tagOptions"
              :selectedValueProp="filterValue"
              placeholder="Select a tag"
              @onSelectOption="onTagSelect"
              :clientSideSearch="true"
            />
          </template>
          <VTextField
            v-else
            type="text"
            aria-label="filter value"
            :maxlength="200"
            :placeholder="filterPlaceholder"
            v-model="filterValue"
            appearance="ghost"
            class="vivid-input"
          />
        </div>
      </div>
    </template>
    <template #action-items>
      <div class="footerBttn">
        <VButton
          class="vvd-btn-primary"
          connotation="cta"
          appearance="filled"
          :label="buttonText"
          :disabled="isFormDisabled"
          @click="isEmailExportType ? submitEmailExport() : downloadFile()"
        ></VButton>
        <VButton
          class="vvd-btn-cancel"
          appearance="filled"
          label="Cancel"
          @click="closeModal"
        ></VButton>
      </div>
    </template>
  </VDialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useUIStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import { useUserData } from '@/composables/useUserData';
import apiInterceptor from '@/shared/apiInterceptor';
import { environment, isLive, isEuServer } from '@/shared/env';
import { customerTagsQueryOptions } from '@/composables/useCustomerTags';
import { queryClient } from '@/shared/queryClient';
import CustomFieldInputKeyValue from '@/components/CustomFieldInputKeyValue.vue';
import selectDropDown from '@/components/selectDropDown.vue';
import HelpTooltip from '@/components/HelpTooltip.vue';

// ── Props & Emits
const props = defineProps({
  isOpen: { type: Boolean, default: false },
  type: { type: String },
  title: { type: String },
  downloadID: { type: String, default: '' },
});

const emit = defineEmits(['close']);

// ── Store
const _authStore = useAuthStore();
const authData = computed(() => _authStore.authData);
const { user } = useUserData();

const updateToast = (payload) => useUIStore().updateToast(payload);

// ── Constants
const EXPORT_FORMAT = 'csv';
const EMAIL_EXPORT_TYPES = new Set(['customers', 'Segments', 'broadcast']);

// ── Dropdown options
const delimiterOptions = [
  { id: 'comma', value: 'comma', title: 'Comma' },
  { id: 'pipe', value: 'pipe', title: 'Pipe' },
  { id: 'semicolon', value: 'semicolon', title: 'Semicolon' },
  { id: 'colon', value: 'colon', title: 'Colon' },
  { id: 'tab', value: 'tab', title: 'Tab' },
];

const filterTypeOptions = [
  { id: 'noFilter', value: 'noFilter', title: '-- No filter --' },
  { id: 'user_name', value: 'user_name', title: 'By Name' },
  { id: 'user_email', value: 'user_email', title: 'By Email' },
  { id: 'user_mobilenumber_wo_plus', value: 'user_mobilenumber_wo_plus', title: 'By Mobile Number' },
  { id: 'CustomTags', value: 'CustomTags', title: 'By Tag' },
  { id: 'CustomFields', value: 'CustomFields', title: 'By Custom Data (Field)' },
  { id: 'marketing_opt_in_status', value: 'marketing_opt_in_status', title: 'By Marketing Opt-in Status' },
];

const marketingOptInOptions = [
  { id: '1', value: '1', title: 'Subscribed' },
  { id: '-1', value: '-1', title: 'Unsubscribed' },
  { id: '0', value: '0', title: 'No status' },
];

// ── State
const selectedDelimiter = ref('comma');
const selectedFilterType = ref('noFilter');
const filterValue = ref(undefined);
const isSubmitting = ref(false);
const isMobileInvalid = ref(false);
const tagOptions = ref([]);
const mobileInputRef = ref(null);

// ── Computed
const isEmailExportType = computed(() => EMAIL_EXPORT_TYPES.has(props.type));

const isFilterValueMissing = computed(() => {
  if (selectedFilterType.value === 'noFilter') return false;
  const value = filterValue.value;
  if (!value) return true;
  if (selectedFilterType.value === 'CustomFields') {
    const [fieldKey] = String(value).split(':');
    return !fieldKey;
  }
  return false;
});

const isFormDisabled = computed(() => isSubmitting.value || isMobileInvalid.value || isFilterValueMissing.value);
const buttonText = computed(() => isSubmitting.value ? 'Exporting...' : 'Export');
const filterPlaceholder = computed(() => {
  const placeholders = {
    user_name: 'Eg.: John Doe',
    user_email: 'Eg.: johndoe@myemail.com',
    user_mobilenumber_wo_plus: 'Eg.: +65 90909090',
    CustomTags: 'Eg.: Subscriber',
    CustomFields: 'Eg.: age:30 (key:value)',
    marketing_opt_in_status: 'Select marketing opt-in status',
  };
  return placeholders[selectedFilterType.value] ?? 'Enter a value';
});

// ── Mobile number validation
const isValidMobileNumber = (mobile) => {
  const match = mobile.match(/^\+(\d{1,3})\s(\d+)$/);
  if (!match) return false;
  const totalDigits = match[1].length + match[2].length;
  return totalDigits >= 4 && totalDigits <= 15;
};

const onMobileInput = (event) => {
  const value = typeof event === 'string' ? event : event?.target?.value || filterValue.value;
  isMobileInvalid.value = !isValidMobileNumber(value || '');
};

// ── Tags
const fetchTags = async () => {
  try {
    // The customer-tags query has staleTime: Infinity, so ensureQueryData would
    // serve a stale cache and miss tags added since (e.g. via CSV import or the
    // customer detail page). Invalidate + fetchQuery re-fetches the latest tags
    // whenever the user selects the Tag filter. [APICC-3482]
    await queryClient.invalidateQueries({ queryKey: customerTagsQueryOptions.queryKey });
    const tags = await queryClient.fetchQuery(customerTagsQueryOptions);
    tagOptions.value = tags.map(tag => ({ title: tag, value: tag, id: tag }));
  } catch {
    tagOptions.value = [];
  }
};

const onTagSelect = (option) => {
  filterValue.value = option.selectedValue;
};

// ── Filter type change
const onFilterTypeChange = () => {
  filterValue.value = undefined;
  isMobileInvalid.value = false;
  if (selectedFilterType.value === 'CustomTags') fetchTags();
};

// ── Reset & Close
const resetForm = () => {
  selectedDelimiter.value = 'comma';
  selectedFilterType.value = 'noFilter';
  filterValue.value = undefined;
  isSubmitting.value = false;
  isMobileInvalid.value = false;
  tagOptions.value = [];
};

const closeModal = () => {
  resetForm();
  emit('close');
};

// ── Build customer filter payload
const buildExportFilter = () => {
  const type = selectedFilterType.value;
  const value = filterValue.value;
  if (!value || type === 'noFilter') return {};

  if (type === 'CustomFields') {
    const [fieldKey, fieldValue] = value.split(':');
    if (!fieldKey) return {};
    return { key: 'custom_field', value: { key: fieldKey, value: fieldValue ?? '' } };
  }

  const filterKeyMap = {
    user_name: 'name',
    user_email: 'email',
    user_mobilenumber_wo_plus: 'mobile',
    CustomTags: 'tags',
    marketing_opt_in_status: 'marketing_opt_in_state',
  };

  const key = filterKeyMap[type];
  if (!key) return {};

  if (type === 'user_mobilenumber_wo_plus') return { key, value: value.replace('+', '') };
  if (type === 'marketing_opt_in_status') return { key, value: Array.isArray(value) ? value.map(v => Number.parseInt(v, 10)) : [Number.parseInt(value, 10)] };
  return { key, value };
};

// ── Download export (teamMembers / FailedImports)
const downloadFile = async () => {
  const endpointMap = {
    teamMembers: `${environment('VUE_APP_API_URL')}roles/v1/team_members/export`,
    FailedImports: `${environment('VUE_APP_API_URL')}crm/v1/customers/import/${props.downloadID}/failed`,
  };

  const baseUrl = endpointMap[props.type];
  if (!baseUrl) {
    updateToast({ open: true, message: 'Invalid export type.', type: 'danger' });
    return;
  }

  try {
    isSubmitting.value = true;
    const host = isLive()
      ? (isEuServer() ? 'dashboard.jumper-emea.com' : 'dashboard.jumperdev.com')
      : 'jumper.ai';
    const apiUrl = `${baseUrl}?${new URLSearchParams({ delimiter_type: selectedDelimiter.value })}`;

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: { Authorization: `JWT ${authData.value.token}`, host },
    });

    const blob = await response.blob();
    const objectUrl = globalThis.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `${user.value.referallshopName}-${props.type}.${EXPORT_FORMAT}`;
    document.body.appendChild(anchor);
    closeModal();
    anchor.click();
    anchor.remove();
    globalThis.URL.revokeObjectURL(objectUrl);
  } catch {
    updateToast({ open: true, message: 'Something went wrong, please try again.', type: 'danger' });
  } finally {
    resetForm();
  }
};

// ── Email export (customers / Segments / broadcast)
const submitEmailExport = async () => {
  try {
    isSubmitting.value = true;
    const apiBase = environment('VUE_APP_API_URL');
    const delimiterParams = { delimiter_type: selectedDelimiter.value };

    if (props.type === 'customers') {
      const filters = buildExportFilter();
      const response = await apiInterceptor.post(`${apiBase}crm/v1/customers/export`, { filters, ...delimiterParams });
      if (!response.success) throw new Error(response.message || 'Export failed');
    } else if (props.type === 'Segments') {
      await apiInterceptor.get(`${apiBase}crm/v1/customers/segments/${props.downloadID}`, { params: delimiterParams });
    } else if (props.type === 'broadcast') {
      await apiInterceptor.get(`${apiBase}broadcasts/v1/broadcasts/${props.downloadID}/export`, { params: delimiterParams });
    }

    updateToast({ open: true, message: "Your report is generating. We'll email you a download link shortly!", type: 'success' });
    closeModal();
  } catch (error) {
    updateToast({ open: true, message: error.message || 'Something went wrong', type: 'danger' });
  } finally {
    resetForm();
  }
};
</script>

<style scoped>
.vivid-input {
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}

.footerBttn {
  display: flex;
  justify-content: center;
  gap: 20px;
  width: 100%;
}
</style>
